import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatCalendar } from '@angular/material/datepicker';
import { Constants } from '@base/src/app/models/constants';
import { QRDatePicker } from '@base/src/app/models/qr-date-picker.model';
import { QRIcon } from '@base/src/app/models/qr-icon.model';
import { Subscription, filter, fromEvent } from 'rxjs';

@Component({
  selector: 'qr-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
})
export class DatePickerComponent implements OnInit {
  @Input() config!: QRDatePicker;
  @Input() isDisabled: boolean = false;
  @Input() hasError: boolean | undefined = false;
  @Output() datepickerChange: EventEmitter<string> = new EventEmitter<string>(); // Se emite en formato base de datos YYYY-MM-DD
  @Output() blurChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  dateVisual: string | undefined; // Fecha visualmente visible para el usuario en formato DD/MM/YYYY
  dateDB: string | undefined; // Fecha para base de datos en formato YYYY-MM-DD
  dateCalendar: Date | null | undefined; // Fecha para el calendario en formato ej: Fri Oct 06 2023 00:00:00 GMT-0300

  hasFormatError: boolean = false; // Indica que hay un error en el formato de la fecha
  hasMinMaxError: boolean = false;
  @Input() isCalendarVisible: boolean = false;

  @ViewChild('calendar', { static: false }) calendar:
    | MatCalendar<Date>
    | undefined;
  readonly ICON_CALENDAR: string = Constants.ICONS.CALENDAR_EVENT;

  readonly ICON_COLOR_GREY_700 = QRIcon.COLOR_GREY_700;
  readonly ICON_COLOR_GREY_400 = QRIcon.COLOR_GREY_400;
  readonly ICON_COLOR_ERROR_900 = QRIcon.COLOR_ERROR_900;
  iconColor: string = this.ICON_COLOR_GREY_700;

  @ViewChild('input', { static: false }) input: ElementRef | undefined;

  inputID: number = this.getRandomCryptographicallyNumber();

  calendarKeyBoardEventSubscription: Subscription | undefined;
  isCalendarOpen: boolean = false;
  minDate: Date | undefined;
  maxDate: Date | undefined;
  positionTop: number | undefined = 50;

  ngOnInit(): void {
    this.calendarKeyBoardEventSubscription = fromEvent<KeyboardEvent>(
      document,
      'keyup'
    )
      .pipe(
        filter(
          (event) =>
            (event.key === 'Escape' || event.key === 'Esc') &&
            this.isCalendarVisible
        )
      )
      .subscribe(() => {
        this.isCalendarVisible = false;
        this.focusInput(this.inputID);
      });
    if (this.config.isDisplayedUp) {
      this.resolveDisplayUp();
    }
  }

  resolveDisplayUp() {
    this.positionTop = 0;
    let calendarWidth: number = this.config.calendarWidth ?? 296;
    const NUMBER = this.config.isDisplayedUp ? -1 : 1;
    this.positionTop = (calendarWidth + 56) * Number(NUMBER);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']?.currentValue['date']) {
      this.dateVisualChange(
        this.dateDBtoVisual(changes['config']?.currentValue['date'])
      ); // Llega desde @Input config la fecha en formato base de datos
    } else if (changes['config']?.currentValue['date'] === '') {
      this.dateVisual = '';
    }
    if (changes['config']?.currentValue['minDate']) {
      this.minDate = this.dateDBToCalendar(
        changes['config'].currentValue['minDate'],
        false
      );
    }
    if (changes['config']?.currentValue['maxDate']) {
      this.maxDate = this.dateDBToCalendar(
        changes['config'].currentValue['maxDate'],
        false
      );
    }
    if (this.config.isDisplayedUp) {
      this.resolveDisplayUp();
    }
    this.updateIconColor();
  }

  ngOnDestroy(): void {
    this.calendarKeyBoardEventSubscription?.unsubscribe();
  }

  /*
  dateVisualChange: Al cambiar la fecha del input manualmente escribiendo
  */
  dateVisualChange(dateVisual: string | undefined): void {
    this.dateVisual = dateVisual;
    this.dateDB = this.dateVisualToDB(this.dateVisual);
    this.dateCalendar = this.dateDBToCalendar(this.dateDB);
    if (
      this.config.isEmptyDateAllowed &&
      (!this.dateVisual || this.dateVisual?.length == 0)
    ) {
      this.hasFormatError = false; // Permitir campo vacío y que no marque error de formato
    }
    this.datepickerChange.emit(this.dateDB);
    this.calendarGoToDate();
    this.updateIconColor();
  }

  /*
  dateCalendarChange: Al cambiar la fecha desde el calendario
  */
  dateCalendarChange(dateCalendar: Date): void {
    this.dateVisual = this.dateCalendarToVisual(dateCalendar.toDateString());
    this.dateDB = this.dateCalendarToDB(dateCalendar.toDateString());
    if (this.dateDB) {
      this.validationDate(this.dateDB);
    }
    this.datepickerChange.emit(this.dateDB);
    this.toggleCalendar();
    this.updateIconColor();
  }

  /*
  calendarGoToDate: Actualiza el calendario para que vaya a la fecha seleccionada
  */
  public calendarGoToDate() {
    setTimeout(() => {
      if (this.dateCalendar) {
        this.calendar?._goToDateInView(this.dateCalendar, 'month');
      }
    }, 10);
  }

  /*
  toggleCalendar: Muestra o oculta el calendario
  */
  toggleCalendar(): void {
    if (!this.isDisabled) {
      this.isCalendarVisible = !this.isCalendarVisible;
      this.blurChange.emit(true);
      if (this.isCalendarVisible) {
        this.calendarGoToDate();
      }
      this.focusInput(this.inputID);
    }
  }

  focusInput(idInput: number): void {
    if (!this.isCalendarVisible) {
      if (
        this.input &&
        this.input.nativeElement.id === idInput.toLocaleString()
      ) {
        this.input?.nativeElement.focus(); // Hace focus sobre el input (Te deja el input seleccionado listo para escribir)
      }
    }
  }

  /*
  getCurrentDateVisual: Obtener fecha actual en formato visual dd/mm/YYYY
  */
  getCurrentDateVisual(): string {
    const currentDate = new Date();
    const day = String(currentDate.getDate()).padStart(2, '0');
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const year = currentDate.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /*
  dateCalendarToDB: Transformar fecha formato calendario ej: Fri Oct 06 2023 00:00:00 GMT-0300
  al formato base de datos YYYY-MM-DD 
  */
  dateCalendarToDB(dateCalendar: string): string | undefined {
    const parsedDate = new Date(dateCalendar);
    if (!isNaN(parsedDate.getTime())) {
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const day = String(parsedDate.getDate()).padStart(2, '0');
      this.hasFormatError = false;
      return `${year}-${month}-${day}`;
    } else {
      this.hasFormatError = true;
      return undefined; // Devuelve undefined para fechas invalidas
    }
  }

  /*
  dateCalendarToVisual: Transformar fecha formato calendario ej: Fri Oct 06 2023 00:00:00 GMT-0300
  al formato visual DD/MM/YYYY 
  */
  dateCalendarToVisual(dateCalendar: string): string | undefined {
    const parsedDate = new Date(dateCalendar);
    if (!isNaN(parsedDate.getTime())) {
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const day = String(parsedDate.getDate()).padStart(2, '0');
      this.hasFormatError = false;
      return `${day}/${month}/${year}`;
    } else {
      this.hasFormatError = true;
      return undefined; // Devuelve undefined para fechas invalidas
    }
  }

  /*
  dateVisualToDB: Transformar la fecha formato visual DD/MM/YYYY a formato
  base de datos YYYY-mm-dd
  */
  dateVisualToDB(dateVisual: string | undefined): string | undefined {
    let dateDB: string | undefined; // YYYY-mm-dd
    if (dateVisual) {
      const pattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
      const regex: RegExp = new RegExp(pattern);
      const match = regex.exec(dateVisual);
      if (match) {
        const [, day, month, year] = match;
        dateDB = `${year}-${month}-${day}`;
        this.hasFormatError = false;
      } else {
        dateDB = undefined;
        this.hasFormatError = true;
      }
    } else {
      dateDB = undefined;
      this.hasFormatError = true;
    }
    return dateDB;
  }

  /*
  dateDBToCalendar: Transformar una fecha formato base de datos ej: YYYY-MM-DD 
  a formato calendario --> Fri Oct 06 2023 00:00:00 GMT-0300
  */
  dateDBToCalendar(
    date: string | undefined,
    isDateSelected: boolean = true
  ): Date | undefined {
    let dateCalendar: Date | undefined;
    if (date) {
      const pattern = /^(\d{4})-(\d{2})-(\d{2})$/; // yyyy-mm-dd
      const regex: RegExp = new RegExp(pattern); // Usar regular expression para match y extraer año, mes y día
      const match = regex.exec(date);
      let dateParts: { day: string; month: string; year: string };
      if (match) {
        const [, year, month, day] = match; // Extraer partes
        // Se verifica año tenga cuatro caracteres y que día y mes tengan dos caracteres
        if (year.length === 4 && month.length === 2 && day.length === 2) {
          dateParts = { year, month, day };
          dateCalendar = new Date(
            +dateParts.year,
            +dateParts.month - 1,
            +dateParts.day,
            +0,
            +0,
            +0
          ); // Genera fecha en formato Nombre del día, Mes, Día, Año, Hora. ej.: Fri Oct 06 2023 00:00:00 GMT-0300
          if (isDateSelected) {
            // Al actualizar la del calendario, se vuelve a actualizar la que figura en el input, esto es para solucionar el bug de si agrega fechas que no existen ej: 32/13/2023
            this.dateVisual = this.dateCalendarToVisual(
              dateCalendar.toDateString()
            );
            this.dateDB = this.dateCalendarToDB(dateCalendar.toDateString());
          }
          this.validationDate(date);
        }
        this.hasFormatError = false;
      } else {
        this.hasFormatError = true;
      }
    }
    return dateCalendar;
  }

  /*
  dateDBtoVisual: Transformar fecha formato base de datos YYYY-MM-DD a formato
  visual DD/MM/YYYY
  */
  dateDBtoVisual(dateDB: string): string | undefined {
    const pattern = /^(\d{4})-(\d{2})-(\d{2})$/;
    const regex: RegExp = new RegExp(pattern);
    const match = regex.exec(dateDB);
    if (match) {
      const [, year, month, day] = match;
      this.hasFormatError = false;
      return `${day}/${month}/${year}`;
    } else {
      this.hasFormatError = true;
      return undefined;
    }
  }

  /*
  keydownChange: Se dispara al presionar un tecla, se utiliza para solo permitir
  ciertas teclas
  */
  keydownChange(event: KeyboardEvent): void {
    const key = event.key;
    if (
      (key >= '0' && key <= '9') || // Números 0-9 (main keyboard)
      (key >= 'NumPad0' && key <= 'NumPad9') || // Números 0-9 (numpad)
      key === '/' ||
      key === 'Delete' ||
      key === 'Backspace' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'Tab' ||
      ((event.ctrlKey || event.metaKey) &&
        (key === 'c' || key === 'v' || key === 'a' || key === 'x')) ||
      (event.shiftKey && key === 'Home')
    ) {
      return; // Permitido
    } else {
      event.preventDefault(); // No permitido
    }
  }

  updateIconColor(): void {
    if (this.hasError || this.hasFormatError || this.hasMinMaxError) {
      this.iconColor = this.ICON_COLOR_ERROR_900;
    } else {
      this.iconColor = this.ICON_COLOR_GREY_700;
    }
    if (this.isDisabled) {
      this.iconColor = this.ICON_COLOR_GREY_400;
    }
  }

  validationDate(date: string): void {
    console.log(date)
    if (this.config.minDate) {
      if (this.validateMinDate(date, this.config.minDate)) {
        this.hasMinMaxError = false;
      } else {
        this.hasMinMaxError = true;
        this.dateDB = undefined; // Se deja como undefined al ser invalida
        return;
      }
    }
    if (this.config.maxDate) {
      if (this.validateMaxDate(date, this.config.maxDate)) {
        this.hasMinMaxError = false;
      } else {
        this.hasMinMaxError = true;
        this.dateDB = undefined; // Se deja como undefined al ser invalida
        return;
      }
    }
  }

  validateMinDate(date: string, minDate: string): boolean {
    const dateSelected: Date = new Date(date);
    const minimumDate: Date = new Date(minDate);
    return dateSelected >= minimumDate;
  }

  validateMaxDate(date: string, maxDate: string): boolean {
    const dateSelected: Date = new Date(date);
    const maximumDate: Date = new Date(maxDate);
    return dateSelected <= maximumDate;
  }

  getRandomCryptographicallyNumber(): number {
    const buf = new Uint8Array(1);
    window.crypto.getRandomValues(buf);
    return buf[0];
  }

  paste(event: ClipboardEvent) {
    event.preventDefault();
    navigator.clipboard.readText().then((pastedText) => {
      const filteredText = pastedText.replace(/[^0-9/]/g, '');
      const inputElement = event.target as HTMLInputElement;
      inputElement.value = filteredText;
    });
  }
}
